import { EventService } from "../../../../../services/event.service";
import { StateService } from "../../../../../services/state.service";
import { S25Const } from "../../../../../util/s25-const";
import { S25Util } from "../../../../../util/s25-util";
import { Item } from "../../../../../pojo/Item";

export type DashQuickSearchRow = {
    itemTypeId: number;
    itemName?: string;
    searchTerm?: string;
    lastItemName?: string;
    isFilled?: boolean;
};

export class DashQuickSearchUtil {
    static focus(model: DashQuickSearchRow) {
        if (!model.lastItemName) {
            Object.assign(model, {
                lastItemName: model.itemName,
                isFilled: true,
            });
            model.itemName = undefined;
        }
    }

    static blur(model: DashQuickSearchRow) {
        if (!model.itemName) {
            model.itemName = model.lastItemName;
            Object.assign(model, {
                lastItemName: undefined,
                isFilled: false,
            });
        }
    }

    static async search(
        model: DashQuickSearchRow,
        context: {
            alert: { too_short_search_term: string; invalid_event_locator: string };
        },
        tzName: string,
        setAlert: (message: string | undefined) => void,
    ) {
        DashQuickSearchUtil.convergeSearchTerm(model);
        if (!DashQuickSearchUtil.validate(model.isFilled, model.searchTerm)) {
            setAlert(context.alert.too_short_search_term);
            if (!model.isFilled) {
                await StateService.gotoItem({ itemTypeId: model.itemTypeId });
            }
            return;
        }
        if (DashQuickSearchUtil.isEventLocator(model.itemTypeId, model.searchTerm)) {
            try {
                const eventId = await EventService.getEventIdByLocator(model.searchTerm!);
                if (DashQuickSearchUtil.validateEventId(eventId)) {
                    DashQuickSearchUtil.clear(model, setAlert);
                    await StateService.gotoItem({ itemTypeId: Item.Ids.Event, itemId: eventId as number });
                    return;
                }
                setAlert(context.alert.invalid_event_locator);
            } catch {
                setAlert(context.alert.invalid_event_locator);
            }
        } else if (DashQuickSearchUtil.isEventLocatorCurrentYear(model.itemTypeId, model.searchTerm)) {
            DashQuickSearchUtil.clear(model, setAlert);
            const locator = DashQuickSearchUtil.getEventLocatorCurrentYear(model.searchTerm!, tzName);
            try {
                const eventId = await EventService.getEventIdByLocator(locator);
                if (DashQuickSearchUtil.validateEventId(eventId)) {
                    await StateService.gotoItem({ itemTypeId: Item.Ids.Event, itemId: eventId as number });
                    return;
                }
                await StateService.gotoItem(model);
            } catch {
                await StateService.gotoItem(model);
            }
        } else {
            DashQuickSearchUtil.clear(model, setAlert);
            await StateService.gotoItem(model);
        }
    }

    private static clear(model: DashQuickSearchRow, setAlert: (message: string | undefined) => void) {
        model.itemName = undefined;
        model.lastItemName = undefined;
        model.isFilled = false;
        setAlert(undefined);
    }

    private static convergeSearchTerm(model: DashQuickSearchRow) {
        model.searchTerm = model.itemName?.trim();
    }

    private static isEventLocator(itemTypeId: number, searchTerm: string | undefined) {
        return DashQuickSearchUtil.isEventSearch(itemTypeId) && !!searchTerm?.match(/^\d{4}\-[A-Za-z]{6}$/);
    }

    private static isEventLocatorCurrentYear(itemTypeId: number, searchTerm: string | undefined) {
        return DashQuickSearchUtil.isEventSearch(itemTypeId) && !!searchTerm?.match(/^[A|a][A-Za-z]{5}$/);
    }

    private static getEventLocatorCurrentYear(searchTerm: string, tzName: string) {
        return S25Util.date.timezone.currentYear(tzName).getFullYear() + "-" + searchTerm;
    }

    private static isEventSearch(itemTypeId: number) {
        const row = S25Const.itemType[itemTypeId as keyof typeof S25Const.itemType];
        return row?.type === "event_search";
    }

    private static validate(isFilled: boolean | undefined, searchTerm: string | undefined) {
        return !!(isFilled && searchTerm && searchTerm.length >= 2);
    }

    private static validateEventId(eventId: unknown) {
        return !S25Util.isUndefined(eventId);
    }
}
